// A signal's definition as one line.
//
// The editor gives every signal a full width form of selects, which is right for
// editing one and wrong for reading fifteen. Within a group only the first
// matching definition is displayed, so the whole list has to be scannable in
// order, and a form per row cannot be scanned. This renders the same conditions
// as a sentence so a collapsed row still says what it tests.
//
// Field names are printed as stored. They are column names, and inventing
// friendlier labels here would put a second vocabulary on screen that matches
// nothing the selects offer.
import { SignalCondition } from '../../../../models/signals';

/** Past this, the values are summarised rather than listed. */
const MAX_VALUES = 2;

function valuesOf(condition: SignalCondition): string {
  const values = (condition?.values ?? []).filter(
    (value) => value !== null && value !== undefined && String(value) !== ''
  );
  if (!values.length) return '';
  if (values.length <= MAX_VALUES) return values.join(', ');
  const shown = values.slice(0, MAX_VALUES).join(', ');
  return `${shown} and ${values.length - MAX_VALUES} more`;
}

/** One condition, as `field verb values`. */
export function signalConditionSentence(condition: SignalCondition): string {
  const field = condition?.field ?? '';
  const verb = condition?.verb ?? '';
  const values = valuesOf(condition);
  // `is known` and `is unknown` take no values, so a trailing space would be the
  // only sign of them.
  return [field, verb, values].filter(Boolean).join(' ');
}

/**
 * The whole definition, with the connectors that join it.
 *
 * The connector belongs to the condition that follows it, which is how
 * `parseConditionsToString` reads them, so it is printed before its own
 * condition rather than after the previous one.
 */
export function signalSentence(conditions: SignalCondition[]): string {
  return (conditions ?? [])
    .map((condition, index) => {
      const sentence = signalConditionSentence(condition);
      if (index === 0) return sentence;
      const connector = String(condition?.direction ?? 'AND').toLowerCase();
      return `${connector} ${sentence}`;
    })
    .filter((part) => part.trim().length)
    .join(' ');
}
